import { Helmet } from "react-helmet-async";

export const SITE_ORIGIN = "https://launchguide.io";

type SEOHeadProps = {
  title: string;
  description: string;
  pathname: string;
  /** When true, adds robots noindex,nofollow (e.g. auth flows). */
  noindex?: boolean;
  /** Optional keywords from webhook payload. */
  keywords?: string[];
  /** Optional JSON-LD object (serialized as one script tag). */
  jsonLd?: Record<string, unknown>;
};

function canonicalUrl(pathname: string): string {
  if (pathname === "/" || pathname === "") return SITE_ORIGIN;
  return `${SITE_ORIGIN}${pathname.startsWith("/") ? pathname : `/${pathname}`}`;
}

/**
 * Per-route document head for crawlers and prerender; uses react-helmet-async.
 */
export function SEOHead({ title, description, pathname, noindex, keywords, jsonLd }: SEOHeadProps) {
  const canonical = canonicalUrl(pathname);
  const keywordsContent = keywords?.filter(Boolean).join(", ");

  return (
    <Helmet htmlAttributes={{ lang: "en" }} prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywordsContent ? <meta name="keywords" content={keywordsContent} /> : null}
      <meta name="author" content="LaunchGuide.io" />
      <link rel="canonical" href={canonical} />
      {noindex ? <meta name="robots" content="noindex, nofollow" /> : null}

      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={`${SITE_ORIGIN}/social-image.png`} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`${SITE_ORIGIN}/social-image.png`} />

      {jsonLd ? (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      ) : null}
    </Helmet>
  );
}
